import { usePostAuthLoginMutation } from '@/api/serverApi'
import { BrandBackground, BrandIcon, Button, Checkbox, InputLabel, Link, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as v from 'valibot'

const loginFormSchema = v.object({
  email: v.string([v.email()]),
  password: v.string([v.minLength(1, 'Password is required')]),
  rememberMe: v.boolean(),
})

type LoginFormInputs = v.Input<typeof loginFormSchema>

const textSpec = {
  title: 'Welcome Back',
  subtext: {
    question: 'Don\'t have an account yet?',
    link: 'Sign Up',
  },
  emailField: {
    label: 'EMAIL ADDRESS',
    placeholder: 'Email',
  },
  passwordField: {
    label: 'PASSWORD',
    placeholder: 'Password',
  },
  forgotPasswordLink: 'Forgot Password?',
  loginButtonLabel: 'Sign In',
  rememberMeLabel: 'Remember me',
}

function LoginForm () {
  const errorHandler = useErrorHandler()
  const navigate = useNavigate()

  const { register, handleSubmit, formState: { errors }, setError } = useForm<LoginFormInputs>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    resolver: valibotResolver(loginFormSchema),
  })

  const [login, { isLoading: isSubmitting }] = usePostAuthLoginMutation()

  const onSubmit: SubmitHandler<LoginFormInputs> = (data) => {
    login({ body: data })
      .unwrap()
      .then((_) => {
        return navigate('/designs')
      })
      .catch((error) => {
        errorHandler(error, setError)
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex items-center flex-col">
        <BrandBackground className="w-[50px] h-[50px]">
          <BrandIcon className="h-[32px] w-[32px] text-grey-800" />
        </BrandBackground>

        <h1 className="mt-[30px] text-4xl leading-[43.2px] font-[450]">{textSpec.title}</h1>

        <p className="mt-[15px] font-medium">
          {textSpec.subtext.question} <Link to="/signup">{textSpec.subtext.link}</Link>
        </p>
      </div>

      <div className="flex flex-col w-96 mt-[50px]">
        <InputLabel name="email">{textSpec.emailField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.emailField.placeholder}
          error={errors?.email?.message}
          {...register('email')}
        />

        <div className="mt-[30px]">
          <InputLabel name="password">{textSpec.passwordField.label}</InputLabel>
          <TextInput
            placeholder={textSpec.passwordField.placeholder}
            type="password"
            error={errors?.password?.message}
            {...register('password')}
          />
        </div>

        <div className="mt-[20px] flex justify-between">
          <Checkbox {...register('rememberMe')}>
            {textSpec.rememberMeLabel}
          </Checkbox>

          <Link
            className="text-[13px]"
            to="/forgot-password"
          >
            {textSpec.forgotPasswordLink}
          </Link>
        </div>


        <div className="mt-[40px]">
          <Button
            variant="dark"
            isLoading={isSubmitting}
            type="submit"
            isBlock
          >
            {textSpec.loginButtonLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default LoginForm
